import type {
  MetaType,
  TypeDefineType,
  PropertyMeta,
  MetaTypeBase,
} from "@/autogen/common";
import type { PaginationResult } from "../../../cbi-generator/generated/dto/PaginationResult";
import type { SortByType } from "../useTableHelper";
import { extractResourceValidationErrors } from "../../../cbi-generator/frontend/errors/ResourceValidationErrors";

export type GeneratedMeta = MetaType<TypeDefineType>;
export type GeneratedProperty = PropertyMeta<MetaTypeBase<unknown>>;
export interface GeneratedAccessor {
  readonly fields: object;
  fieldErrors: Record<string, string>;
}
export type FieldOverride = {
  meta?: GeneratedProperty;
  label?: string;
  hidden?: boolean;
  readonly?: boolean;
};
/** Renders the listed fields side by side, in order, under an optional heading. */
export type FieldLayoutGroup = {
  key: string;
  title?: string;
  fields: string[];
};
export type FieldOverrides<F extends object> = Partial<
  Record<Extract<keyof F, string>, FieldOverride>
>;
export type FieldCustomization<F extends object> =
  | FieldOverrides<F>
  | ((fields: Readonly<F>) => FieldOverrides<F>);
export interface GeneratedEditorOptions<
  Row extends object,
  Read extends object,
  A extends GeneratedAccessor,
> {
  identify: (row: Row) => number;
  read: (id: number) => Promise<Read>;
  createAccessor: (defaults: Partial<A["fields"]>) => A;
  readAccessor: (record: Read) => A;
  save: (accessor: A) => Promise<Read>;
  remove?: (id: number) => Promise<unknown>;
  defaults?: Partial<A["fields"]> | (() => Partial<A["fields"]>);
}
export interface GeneratedTableConfig<
  Row extends object,
  Params extends object,
> {
  id: string;
  rowKey: Extract<keyof Row, string>;
  resultMeta: GeneratedMeta;
  searchMeta: GeneratedMeta;
  initialFilter: () => Params;
  search: (
    params: Params,
    page: number,
    perPage: number,
  ) => Promise<PaginationResult<Row>>;
  filterFields?: FieldOverrides<Params>;
  visibleColumns?: string[];
  defaultSort?: SortByType[];
}
export interface GeneratedCrudConfig<
  Row extends object,
  Params extends object,
  Read extends object,
  A extends GeneratedAccessor,
>
  extends
    GeneratedEditorOptions<Row, Read, A>,
    GeneratedTableConfig<Row, Params> {
  title: string;
  createMeta: GeneratedMeta;
  updateMeta: GeneratedMeta;
  fields?: FieldCustomization<A["fields"]>;
}

export function errorMessage(error: unknown): string {
  const validation = extractResourceValidationErrors(error);
  if (validation?.length)
    return validation.map((item) => item.message).join(" ");
  const staffMessage = isAxiosError<{ snackBarError?: unknown }>(error)
    ? error.response?.data?.snackBarError
    : null;
  if (typeof staffMessage === "string" && staffMessage.trim())
    return staffMessage;
  return error instanceof Error && error.message
    ? error.message
    : "The request failed. Please try again.";
}

export function validationFieldErrors(
  error: unknown,
  prefix = "",
): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const item of extractResourceValidationErrors(error) ?? []) {
    if (item.field?.startsWith(prefix))
      fields[item.field.slice(prefix.length)] = item.message;
  }
  return fields;
}
import { isAxiosError } from "axios";
